import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ChevronDown,
  ChevronLeft,
  HelpCircle,
  LogOut,
  Menu,
  ShieldAlert,
  WifiOff,
  X,
} from 'lucide-react'
import clsx from 'clsx'

import { alCambiarLaApi, laApiResponde } from '@/lib/api'
import { useSesion } from '@/lib/sesion'
import { esCaja } from '@/lib/config'
import { sesionSuplantada } from '@/lib/suplantacion'
import { EstadoSincronizacion } from './EstadoSincronizacion'
import { useAyuda } from '@/lib/ayuda'
import { CajonAyuda } from '@/componentes/ayuda/CajonAyuda'
import { Confirmacion } from '@/componentes/ui/Confirmacion'
import { DialogoElevacion } from '@/componentes/ui/DialogoElevacion'
import { DialogoMiPin } from '@/features/auth/DialogoMiPin'
import { api } from '@/lib/api'
import { armarMenu, consultaMenu, type ItemMenu } from '@/lib/menu'
import { useModulos } from '@/lib/modulos'

export function Layout() {
  const { slug } = useParams<{ slug: string }>()
  const ubicacion = useLocation()

  // En el ejecutable de la caja las rutas son planas: no hay slug en la
  // dirección porque el programa está registrado contra un solo local, y
  // meterlo en la URL solo daría lugar a que alguien la edite.
  const raiz = esCaja() ? '' : `/${slug}`
  const { usuario, tenant, salir, puede } = useSesion()

  // Mi cuenta: por ahora, cambiarse el PIN. Se consulta al abrir el diálogo y
  // no al montar el layout, porque es un dato que solo esa pantalla mira y
  // pedirlo en cada carga del sistema sería un viaje por nada.
  const [miPin, setMiPin] = useState(false)

  // El logo del local vive en la base y se sirve por su Code (nunca una URL de
  // afuera: sin internet sería un cuadrado roto). `urlImagen` le pone el
  // prefijo de la API, que en la caja del mostrador es 127.0.0.1.
  const logoLocal = api.urlImagen(tenant?.logoUrl)
  const navegar = useNavigate()

  const [menuAbierto, setMenuAbierto] = useState(false)
  const [contraido, setContraido] = useState(() => {
    try {
      return localStorage.getItem('fruver.sidebar') === 'contraido'
    } catch {
      return false
    }
  })

  const sinInternet = useSinInternet()
  const alternarAyuda = useAyuda((s) => s.alternar)

  // El atajo F1 lo escucha el propio `CajonAyuda`: así lo tiene cualquier
  // pantalla que lo muestre, y no solo las que cuelgan de este layout.

  // El POS ocupa la ventana entera y maneja su propio scroll. Se detecta por
  // la ruta y no con un contexto porque son dos rutas y un contexto para eso
  // es andamiaje que hay que explicar.
  //
  // Son DOS rutas y no tres: en el ejecutable de la caja el POS es la raíz, y
  // en la web es `/{slug}/caja`. La raíz de la WEB es el tablero, que es una
  // pantalla de documento y tiene que scrollear —estaba entrando acá, así que
  // el Inicio quedaba con `overflow-hidden` y en un teléfono no se podía
  // bajar: las tarjetas de abajo no existían—. No se nota en el escritorio,
  // donde el tablero entra en la ventana, que es exactamente por qué
  // sobrevivió.
  const pantallaCompleta =
    ubicacion.pathname.endsWith('/caja') || (esCaja() && ubicacion.pathname === '/')

  useEffect(() => {
    try {
      localStorage.setItem('fruver.sidebar', contraido ? 'contraido' : 'abierto')
    } catch {
      /* nada */
    }
  }, [contraido])

  // El menú viene del servidor y se resuelve contra el catálogo de pantallas.
  //
  // Mientras no llegó (primer render, o la caja sin haber sincronizado) se
  // arma con los valores de fábrica: un menú vacío durante un segundo se ve
  // como un sistema roto.
  const { data: configMenu } = useQuery(consultaMenu)

  // Y los módulos que el local usa: los que apagó no tienen pantalla, así que
  // tampoco tienen ítem (ver lib/modulos.ts).
  const { modulos } = useModulos()

  const items = armarMenu(configMenu ?? [], puede, modulos)

  const cerrar = async () => {
    await salir()

    // En la caja no hay pantalla de login con slug: salir vuelve a la
    // selección de usuario, que es lo que renderiza App cuando no hay sesión.
    void navegar(esCaja() ? '/' : `/${slug}/login`, { replace: true })
  }

  return (
    // h-screen y no min-h-screen: hay pantallas que necesitan ocupar
    // EXACTAMENTE el alto de la ventana y manejar su propio scroll adentro. El
    // POS es la principal: si el pie con el total y el botón de cobrar queda
    // abajo del pliegue, el cajero tiene que scrollear para cobrar.
    <div className="flex h-screen overflow-hidden bg-crema">
      {/* Barra lateral */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex flex-col bg-tinta text-crema transition-all duration-200',
          'lg:static lg:translate-x-0',
          contraido ? 'lg:w-[68px]' : 'lg:w-[228px]',
          menuAbierto ? 'w-[260px] translate-x-0' : '-translate-x-full',
        )}
      >
        {/* La marca del LOCAL, no la del producto.
            §1.1: el nombre y el logo salen del tenant y nunca de un literal.
            Acá decía «Fruver.», o sea que la barra lateral de cada verdulería
            mostraba el nombre del sistema y no el del negocio —y el logo que
            el dueño podía cargar no se veía en ninguna pantalla del sistema,
            solo en la de entrada—. */}
        <div className="flex h-[58px] shrink-0 items-center gap-2 px-3">
          {logoLocal ? (
            <img
              src={logoLocal}
              alt=""
              className="h-8 w-8 shrink-0 rounded-lg bg-crema/10 object-contain p-0.5"
            />
          ) : (
            <span
              className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-verde
                         text-[12px] font-bold text-white"
              aria-hidden
            >
              {inicialesDelLocal(tenant?.nombre)}
            </span>
          )}

          {!contraido && (
            <span className="truncate text-[14.5px] font-bold tracking-[-0.01em]">
              {tenant?.nombre ?? ''}
            </span>
          )}

          <button
            type="button"
            onClick={() => setMenuAbierto(false)}
            className="ml-auto rounded p-1.5 text-crema/60 hover:bg-white/10 lg:hidden"
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
          {items.map((i) => (
            <ItemDelMenu
              key={i.clave}
              item={i}
              raiz={raiz}
              contraido={contraido}
              onNavegar={() => setMenuAbierto(false)}
            />
          ))}
        </nav>

        <div className="shrink-0 border-t border-white/[0.08] p-2">
          <button
            type="button"
            onClick={() => setContraido((c) => !c)}
            className="hidden w-full items-center gap-3 rounded px-3 py-2 text-[13px] text-crema/50
                       hover:bg-white/[0.07] hover:text-crema lg:flex"
          >
            <ChevronLeft className={clsx('h-4 w-4 transition-transform', contraido && 'rotate-180')} />
            <span className={clsx(contraido && 'lg:hidden')}>Contraer</span>
          </button>

          <button
            type="button"
            onClick={cerrar}
            className={clsx(
              'flex w-full items-center gap-3 rounded px-3 py-2.5 text-[14px] text-crema/70 hover:bg-white/[0.07] hover:text-crema',
              contraido && 'lg:justify-center lg:px-2',
            )}
          >
            <LogOut className="h-[18px] w-[18px]" />
            <span className={clsx(contraido && 'lg:hidden')}>Salir</span>
          </button>
        </div>
      </aside>

      {menuAbierto && (
        <div
          className="fixed inset-0 z-30 bg-tinta/40 lg:hidden"
          onClick={() => setMenuAbierto(false)}
        />
      )}

      {/* Contenido */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-[58px] shrink-0 items-center gap-3 border-b border-borde bg-crema/95 px-4 backdrop-blur">
          <button
            type="button"
            onClick={() => setMenuAbierto(true)}
            className="rounded p-2 text-tinta-suave hover:bg-crema-alt lg:hidden"
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </button>

          <span className="truncate text-[14.5px] font-semibold text-tinta">{tenant?.nombre}</span>

          <div className="flex-1" />

          {/* En la caja del mostrador el estado que importa es el del enlace
              con la NUBE, no el del navegador: la API local está siempre
              arriba, así que el cartel de abajo nunca se encendería y el
              cajero no tendría forma de saber que sus ventas se están
              acumulando. Ver EstadoSincronizacion. */}
          <EstadoSincronizacion />

          {/* Cartel de sin internet: grande, ámbar y con texto, no un iconito
              gris. Si el cajero no se da cuenta de que está offline, después
              nadie entiende por qué faltan ventas.

              No va en la caja: ahí lo dice el estado de sincronización, que
              además sabe cuántas ventas faltan subir. */}
          {sinInternet && !esCaja() && (
            <div className="flex items-center gap-2 rounded-lg bg-ambar px-3 py-1.5 text-[12.5px] font-semibold text-white">
              <WifiOff className="h-4 w-4" />
              <span className="hidden sm:inline">SIN INTERNET</span>
            </div>
          )}

          {/* Ayuda de la pantalla. Va en el encabezado y no escondido en un
              menú: una ayuda que hay que buscar no se usa. */}
          <button
            type="button"
            onClick={alternarAyuda}
            className="rounded-full p-2 text-tinta-suave transition-colors hover:bg-crema-alt hover:text-tinta"
            aria-label="Ayuda de esta pantalla"
            title="Ayuda de esta pantalla (F1)"
          >
            <HelpCircle className="h-[19px] w-[19px]" />
          </button>

          {/* La ficha de quien está adentro es un BOTÓN, no un cartel.
              Era el único lugar del sistema donde aparece el nombre de uno
              mismo y no llevaba a ningún lado: el cajero, que no tiene
              ninguna pantalla de configuración a la que entrar, no tenía cómo
              cambiarse el PIN que le eligió otro. */}
          <button
            type="button"
            onClick={() => setMiPin(true)}
            title="Mi cuenta"
            className="flex items-center gap-2.5 rounded-full bg-crema-alt py-1 pl-1 pr-3
                       transition-colors hover:bg-borde"
          >
            {usuario?.fotoUrl ? (
              <img
                src={api.urlImagen(usuario.fotoUrl)}
                alt=""
                className="h-7 w-7 rounded-full object-cover"
              />
            ) : (
              <span className="grid h-7 w-7 place-items-center rounded-full bg-verde text-[11px] font-bold text-white">
                {usuario?.iniciales}
              </span>
            )}

            <span className="hidden text-[13px] font-medium text-tinta sm:inline">
              {usuario?.nombre.split(' ')[0]}
              <span className="ml-1.5 text-tinta-tenue">· {usuario?.rol}</span>
            </span>
          </button>
        </header>

        <BandaSuplantacion />

        {/* El padding y el scroll son de las pantallas «de documento»
            (tablero, productos, configuración). Las que se manejan solas —el
            POS— lo declaran con `pantallaCompleta` y acá se les da el alto
            entero sin margen. */}
        <main
          className={clsx(
            'min-w-0 flex-1',
            pantallaCompleta ? 'min-h-0 overflow-hidden' : 'overflow-y-auto p-4 lg:p-6',
          )}
        >
          <Outlet />
        </main>
      </div>

      <CajonAyuda />
      <Confirmacion />
      <DialogoElevacion />

      {miPin && <DialogoMiPin onCerrar={() => setMiPin(false)} />}
    </div>
  )
}

/**
 * Las iniciales del local, para cuando no cargó su logo.
 *
 * Dos letras y no una: con una, «Verdulería La Esquina» y «Verdulería Don
 * Pepe» se ven iguales, y quien administra dos locales en dos pestañas no
 * distingue en cuál está.
 */
/**
 * Un ítem del menú, con sus submenús si los tiene.
 *
 * Configuración eran once solapas adentro de una sola pantalla: para ir a
 * «Roles» había que entrar a Configuración y recién ahí elegir, y desde el menú
 * no se veía que existieran. Ahora cuelgan de acá.
 *
 * Tres cosas de la forma:
 *
 * - **El padre sigue siendo un enlace**, no una carpeta que solo abre y cierra.
 *   Lleva a la primera pantalla que la persona puede abrir, así un favorito
 *   viejo de `/config` sigue funcionando y un toque no queda sin efecto.
 * - **Se despliega solo cuando estás adentro**, y se puede plegar a mano. Que
 *   arranque cerrado en las otras pantallas es lo que evita que un menú de
 *   quince ítems pase a tener veintiséis.
 * - **Contraída la barra, no hay submenú**: quedan 68 px de ancho y un ítem
 *   indentado ahí no se distingue de uno del nivel de arriba. El padre lleva a
 *   su primera pantalla, que es desde donde se llega al resto.
 */
function ItemDelMenu({
  item,
  raiz,
  contraido,
  onNavegar,
}: {
  item: ItemMenu
  raiz: string
  contraido: boolean
  onNavegar: () => void
}) {
  const ubicacion = useLocation()

  const hijos = item.hijos ?? []
  const destino = (a: string) => (a ? `${raiz}/${a}` : raiz || '/')

  // «Estoy adentro» se mira por la RUTA y no por el NavLink del padre: el padre
  // apunta a la primera pantalla del grupo, así que estando en «Roles» no
  // estaría activo y el grupo se cerraría solo justo donde se lo necesita
  // abierto.
  const adentro = hijos.length > 0 && ubicacion.pathname.startsWith(`${destino(item.a)}`)

  const [abierto, setAbierto] = useState(adentro)

  // Entrar al grupo desde otro lado (un enlace, el botón de atrás) lo abre.
  // Es estado derivado y por eso se compara antes de escribir: sin el corte,
  // esto sería un render en cascada en cada navegación.
  const [eraAdentro, setEraAdentro] = useState(adentro)

  if (adentro !== eraAdentro) {
    setEraAdentro(adentro)
    if (adentro) setAbierto(true)
  }

  const enlace = (i: ItemMenu, hijo: boolean) => (
    <NavLink
      key={i.clave}
      to={destino(i.a)}
      end={!i.a || hijo}
      onClick={onNavegar}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 rounded px-3 py-2.5 text-[14px] font-medium transition-colors',
          hijo && 'py-2 pl-9 text-[13.5px]',
          isActive ? 'bg-verde text-white' : 'text-crema/70 hover:bg-white/[0.07] hover:text-crema',
          contraido && 'lg:justify-center lg:px-2',
        )
      }
      title={contraido ? i.titulo : undefined}
    >
      {!(hijo && !contraido) && <i.icono className="h-[18px] w-[18px] shrink-0" />}
      <span className={clsx(contraido && 'lg:hidden')}>{i.titulo}</span>
    </NavLink>
  )

  if (hijos.length === 0 || contraido) return enlace(item, false)

  return (
    <div>
      <div className="flex items-center gap-0.5">
        <div className="min-w-0 flex-1">{enlace(item, false)}</div>

        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          aria-label={`${abierto ? 'Plegar' : 'Desplegar'} ${item.titulo}`}
          className="shrink-0 rounded p-1.5 text-crema/50 hover:bg-white/[0.07] hover:text-crema"
        >
          <ChevronDown className={clsx('h-4 w-4 transition-transform', !abierto && '-rotate-90')} />
        </button>
      </div>

      {abierto && <div className="mt-0.5 space-y-0.5">{hijos.map((h) => enlace(h, true))}</div>}
    </div>
  )
}

function inicialesDelLocal(nombre?: string): string {
  const partes = (nombre ?? '').split(' ').filter(Boolean)

  if (partes.length === 0) return '—'
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase()

  return `${partes[0]![0]}${partes[partes.length - 1]![0]}`.toUpperCase()
}

// La banda de «alguien de soporte está adentro».
//
// Va debajo del encabezado y en un color que no se usa para nada normal: quien
// entró desde el panel de plataforma tiene que tener siempre a la vista que
// está en el sistema de OTRO, con los permisos del dueño, y que cada cosa que
// haga queda a su nombre en la bitácora del local.
//
// No es solo cortesía: sin la banda, una pestaña olvidada se confunde con la
// propia y se termina tocando la configuración del local equivocado.
function BandaSuplantacion() {
  const suplantada = sesionSuplantada()

  if (!suplantada) return null

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-ambar/40 bg-ambar/15 px-4 py-1.5 text-[12.5px] text-tinta">
      <span className="flex items-center gap-1.5 font-semibold">
        <ShieldAlert className="h-3.5 w-3.5 text-ambar" />
        Soporte de plataforma
      </span>

      <span className="text-tinta-suave">
        Estás en «{suplantada.tenantNombre}» como {suplantada.comoUsuario}. Todo lo que hagas queda
        anotado en la bitácora del local.
      </span>

      <a href="/plataforma" className="ml-auto font-semibold text-tinta underline">
        Volver al panel
      </a>
    </div>
  )
}

// Detecta la falta de conexión.
//
// Se miran DOS cosas, y la segunda es la que importa: `navigator.onLine` dice
// «online» cuando hay wifi pero el módem no llega a internet, que es EL caso
// del local. Con solo eso, el cartel no aparecía nunca y el cajero se enteraba
// al cobrar, con el cliente adelante.
//
// La segunda señal es si LA API contesta, y sale de los propios pedidos del
// sistema (ver `alCambiarLaApi` en lib/api): no hay sondeo ni tráfico extra, y
// la pregunta es la correcta —no «hay internet» sino «el servidor responde»—,
// porque un local puede tener internet perfecto y la API caída, y para el
// cajero es lo mismo.
function useSinInternet() {
  const [sinRed, setSinRed] = useState(!navigator.onLine)
  const [apiCaida, setApiCaida] = useState(!laApiResponde())

  useEffect(() => {
    const online = () => setSinRed(false)
    const offline = () => setSinRed(true)

    window.addEventListener('online', online)
    window.addEventListener('offline', offline)

    const darDeBaja = alCambiarLaApi((responde) => setApiCaida(!responde))

    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
      darDeBaja()
    }
  }, [])

  return sinRed || apiCaida
}
